import React, { useState, useEffect } from 'react';
import { contentAPI } from '../../api/content';
import { toast } from 'react-toastify';
import './SiteSettings.css';

import { getApiBaseUrl } from '../../api/apiConfig';

const SiteSettings = () => {
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('hero');

  useEffect(() => {
    loadSettings();
  }, []);

  const loadSettings = async () => {
    try {
      const data = await contentAPI.getFlat();
      setSettings(data);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (key, value) => {
    setSettings(prev => ({ ...prev, [key]: value }));
  };

  const handleImageUpload = async (e, field) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File too large. Max 5MB.');
      return;
    }
    try {
      const res = await contentAPI.uploadImage(field, file);
      handleChange(field, res.url);
      toast.success('Image uploaded');
    } catch (err) {
      toast.error('Upload failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await contentAPI.update(settings);
      toast.success('Settings saved successfully');
    } catch (err) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const imgUrl = (path) => path ? `${getApiBaseUrl()}${path.startsWith('/') ? path : '/' + path}` : null;

  if (loading) return <div className="site-settings-loading">Loading settings...</div>;

  const tabs = [
    { id: 'hero', label: 'Hero Section' },
    { id: 'general', label: 'General' },
    { id: 'about', label: 'About' },
    { id: 'portfolio', label: 'Portfolio' },
    { id: 'stats', label: 'Statistics' },
    { id: 'cta', label: 'Call-to-Action' },
    { id: 'contact', label: 'Contact' },
    { id: 'social', label: 'Social Media' },
    { id: 'footer', label: 'Footer' },
  ];

  return (
    <div className="site-settings-container">
      <div className="site-settings-header">
        <h1>Site Settings</h1>
        <p>Manage all website content</p>
      </div>

      <div className="site-settings-tabs">
        {tabs.map(t => (
          <button
            key={t.id}
            className={activeTab === t.id ? 'active' : ''}
            onClick={() => setActiveTab(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      <form onSubmit={handleSubmit} className="site-settings-form">
        {activeTab === 'hero' && (
          <div className="settings-section">
            <h2>Hero Section</h2>
            <div className="form-group">
              <label>Hero Title</label>
              <input
                value={settings.hero_title || ''}
                onChange={e => handleChange('hero_title', e.target.value)}
                placeholder="Elevating Your Vision Through Creative Excellence"
              />
            </div>
            <div className="form-group">
              <label>Hero Subtitle (before typewriter)</label>
              <input
                value={settings.hero_subtitle || ''}
                onChange={e => handleChange('hero_subtitle', e.target.value)}
                placeholder="Specializing in"
              />
            </div>
            <div className="form-group">
              <label>Typewriter Words (comma-separated)</label>
              <input
                value={settings.typewriter_words || ''}
                onChange={e => handleChange('typewriter_words', e.target.value)}
                placeholder="Professional Photography, Videography, ..."
              />
            </div>
            <div className="form-group">
              <label>Hero Description</label>
              <textarea
                value={settings.hero_description || ''}
                onChange={e => handleChange('hero_description', e.target.value)}
                rows="4"
              />
            </div>
            <div className="form-group">
              <label>Hero Image</label>
              <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'hero_image')} />
              {imgUrl(settings.hero_image) && (
                <div className="img-preview">
                  <img src={imgUrl(settings.hero_image)} alt="Hero" />
                </div>
              )}
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CTA Text</label>
                <input
                  value={settings.hero_cta_text || ''}
                  onChange={e => handleChange('hero_cta_text', e.target.value)}
                  placeholder="Hire Us"
                />
              </div>
              <div className="form-group">
                <label>CTA Link</label>
                <input
                  value={settings.hero_cta_link || ''}
                  onChange={e => handleChange('hero_cta_link', e.target.value)}
                  placeholder="/hireMe"
                />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'general' && (
          <div className="settings-section">
            <h2>General</h2>
            <div className="form-group">
              <label>Site Name</label>
              <input
                value={settings.site_name || ''}
                onChange={e => handleChange('site_name', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Meta Description</label>
              <textarea
                value={settings.meta_description || ''}
                onChange={e => handleChange('meta_description', e.target.value)}
                rows="2"
              />
            </div>
            <div className="form-group">
              <label>Meta Keywords</label>
              <input
                value={settings.meta_keywords || ''}
                onChange={e => handleChange('meta_keywords', e.target.value)}
              />
            </div>
          </div>
        )}

        {activeTab === 'about' && (
          <div className="settings-section">
            <h2>About / What We Do</h2>
            <div className="form-group">
              <label>Section Title</label>
              <input
                value={settings.about_title || ''}
                onChange={e => handleChange('about_title', e.target.value)}
                placeholder="What We Do"
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={settings.about_description || ''}
                onChange={e => handleChange('about_description', e.target.value)}
                rows="5"
              />
            </div>
            <div className="form-group">
              <label>About Image</label>
              <input type="file" accept="image/*" onChange={e => handleImageUpload(e, 'about_image')} />
              {imgUrl(settings.about_image) && (
                <div className="img-preview"><img src={imgUrl(settings.about_image)} alt="About" /></div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'portfolio' && (
          <div className="settings-section">
            <h2>Portfolio Section</h2>
            <div className="form-group">
              <label>Title</label>
              <input
                value={settings.portfolio_title || ''}
                onChange={e => handleChange('portfolio_title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={settings.portfolio_description || ''}
                onChange={e => handleChange('portfolio_description', e.target.value)}
                rows="3"
              />
            </div>
            <div className="form-group">
              <label>Items to Show</label>
              <input
                type="number"
                min="1"
                max="20"
                value={settings.portfolio_items_to_show || '6'}
                onChange={e => handleChange('portfolio_items_to_show', e.target.value)}
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>CTA Text</label>
                <input value={settings.portfolio_cta_text || ''} onChange={e => handleChange('portfolio_cta_text', e.target.value)} />
              </div>
              <div className="form-group">
                <label>CTA Link</label>
                <input value={settings.portfolio_cta_link || ''} onChange={e => handleChange('portfolio_cta_link', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'stats' && (
          <div className="settings-section">
            <h2>Statistics</h2>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="stats_enabled"
                checked={settings.stats_enabled === '1'}
                onChange={e => handleChange('stats_enabled', e.target.checked ? '1' : '0')}
              />
              <label htmlFor="stats_enabled">Enable Statistics Section</label>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Years Experience</label>
                <input type="number" value={settings.stat_years_experience || ''} onChange={e => handleChange('stat_years_experience', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Happy Clients</label>
                <input type="number" value={settings.stat_happy_clients || ''} onChange={e => handleChange('stat_happy_clients', e.target.value)} />
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Projects Completed</label>
                <input type="number" value={settings.stat_projects_completed || ''} onChange={e => handleChange('stat_projects_completed', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Awards Won</label>
                <input type="number" value={settings.stat_awards_won || ''} onChange={e => handleChange('stat_awards_won', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'cta' && (
          <div className="settings-section">
            <h2>Call-to-Action</h2>
            <div className="form-group checkbox">
              <input
                type="checkbox"
                id="cta_enabled"
                checked={settings.cta_enabled === '1'}
                onChange={e => handleChange('cta_enabled', e.target.checked ? '1' : '0')}
              />
              <label htmlFor="cta_enabled">Enable CTA Section</label>
            </div>
            <div className="form-group">
              <label>Title</label>
              <input value={settings.cta_title || ''} onChange={e => handleChange('cta_title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea value={settings.cta_description || ''} onChange={e => handleChange('cta_description', e.target.value)} rows="2" />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>Button Text</label>
                <input value={settings.cta_button_text || ''} onChange={e => handleChange('cta_button_text', e.target.value)} />
              </div>
              <div className="form-group">
                <label>Button Link</label>
                <input value={settings.cta_button_link || ''} onChange={e => handleChange('cta_button_link', e.target.value)} />
              </div>
            </div>
          </div>
        )}

        {activeTab === 'contact' && (
          <div className="settings-section">
            <h2>Contact Info</h2>
            <div className="form-group">
              <label>Title</label>
              <input value={settings.contact_title || ''} onChange={e => handleChange('contact_title', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Location</label>
              <input value={settings.contact_location || ''} onChange={e => handleChange('contact_location', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input type="email" value={settings.contact_email || ''} onChange={e => handleChange('contact_email', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Phone</label>
              <input value={settings.contact_phone || ''} onChange={e => handleChange('contact_phone', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Timezone</label>
              <input value={settings.contact_timezone || ''} onChange={e => handleChange('contact_timezone', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Office Hours</label>
              <textarea value={settings.contact_office_hours || ''} onChange={e => handleChange('contact_office_hours', e.target.value)} rows="2" />
            </div>
          </div>
        )}

        {activeTab === 'social' && (
          <div className="settings-section">
            <h2>Social Media</h2>
            {[
              { key: 'instagram', label: 'Instagram', placeholder: 'https://instagram.com/...' },
              { key: 'linkedin', label: 'LinkedIn', placeholder: 'https://linkedin.com/...' },
              { key: 'youtube', label: 'YouTube', placeholder: 'https://youtube.com/...' },
              { key: 'facebook', label: 'Facebook', placeholder: 'https://facebook.com/...' },
              { key: 'tiktok', label: 'TikTok', placeholder: 'https://tiktok.com/...' },
              { key: 'twitter', label: 'Twitter / X', placeholder: 'https://twitter.com/...' },
              { key: 'behance', label: 'Behance', placeholder: 'https://behance.net/...' },
              { key: 'whatsapp', label: 'WhatsApp', placeholder: 'https://wa.me/254790798042' },
            ].map(({ key, label, placeholder }) => (
              <div key={key} className="form-group">
                <label>{label}</label>
                <input
                  type="url"
                  value={settings[`social_${key}`] || ''}
                  onChange={e => handleChange(`social_${key}`, e.target.value)}
                  placeholder={placeholder}
                />
              </div>
            ))}
          </div>
        )}

        {activeTab === 'footer' && (
          <div className="settings-section">
            <h2>Footer</h2>
            <div className="form-group">
              <label>Brand Name</label>
              <input value={settings.footer_brand || ''} onChange={e => handleChange('footer_brand', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Tagline</label>
              <input value={settings.footer_tagline || ''} onChange={e => handleChange('footer_tagline', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Copyright</label>
              <input value={settings.footer_copyright || ''} onChange={e => handleChange('footer_copyright', e.target.value)} />
            </div>
            <div className="form-group">
              <label>Status</label>
              <input value={settings.footer_status || ''} onChange={e => handleChange('footer_status', e.target.value)} placeholder="Currently Booking" />
            </div>
          </div>
        )}

        <div className="form-actions">
          <button type="submit" className="btn-primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default SiteSettings;
